import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { USER_LEVEL, USER_ROLE, type PinMessage } from '@fdh/shared';
import { AppPageTitleSection } from '../../components/ui/AppPageTitleSection';
import { PagePrimaryFab } from '../../components/ui/PagePrimaryFab';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../components/ui/ToastProvider';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import type { MessageParams } from '../../i18n/uiMessages';
import { pinMessageApi } from '../../lib/api';
import { emitPinMessageActiveLimitIfReached, isLimitReachedError } from '../../lib/limitError';
import { usePinMessageCreationGate } from '../../hooks/usePinMessageCreationGate';
import { pinMessageIconLabelKey, pinMessageIconSvg, pinMessageIconTheme } from '../../components/pinMessage/pinMessageIconTheme';

const iconLabel = (iconType: PinMessage['iconType'], t: (key: string, params?: MessageParams) => string): string =>
  t(pinMessageIconLabelKey(iconType));

const visibilityLabel = (visibility: PinMessage['visibility'], t: (key: string, params?: MessageParams) => string): string =>
  visibility === 'both' ? t('pinMessage.visibility.both') : t('pinMessage.visibility.employerOnly');

const windowLabel = (item: PinMessage, t: (key: string, params?: MessageParams) => string): string => {
  if (!item.displayFromDate && !item.displayToDate) {
    return t('pinMessage.window.always');
  }
  if (item.displayFromDate && item.displayToDate) {
    return t('pinMessage.window.range', { from: item.displayFromDate, to: item.displayToDate });
  }
  if (item.displayFromDate) {
    return t('pinMessage.window.from', { from: item.displayFromDate });
  }
  return t('pinMessage.window.to', { to: item.displayToDate });
};

const PencilIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
    />
  </svg>
);

const TrashIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

export const PinMessageListPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { showErrorToast, showSuccessToast } = useToast();
  const navigate = useNavigate();
  const { guardNavigateToCreatePinMessage } = usePinMessageCreationGate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState<PinMessage[]>([]);
  const [maxItems, setMaxItems] = useState(20);
  const [draggingId, setDraggingId] = useState('');
  const [savingOrder, setSavingOrder] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [listRes, limitRes] = await Promise.all([
        pinMessageApi.list(true),
        pinMessageApi.getLimits(),
      ]);
      if (listRes.success && listRes.data) {
        setItems(listRes.data.items ?? []);
      } else {
        setItems([]);
      }
      if (limitRes.success && limitRes.data) {
        setMaxItems(limitRes.data.maxItems);
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('pinMessage.error.loadList'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const orderedIds = useMemo(() => items.map((item) => item.id), [items]);

  const handleSaveOrder = useCallback(async (nextItems: PinMessage[]) => {
    setItems(nextItems);
    setSavingOrder(true);
    setError('');
    try {
      await pinMessageApi.reorder({ orderedIds: nextItems.map((item) => item.id) });
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('pinMessage.error.saveOrder'));
      await load();
    } finally {
      setSavingOrder(false);
    }
  }, [load, t]);

  const moveItem = useCallback((id: string, delta: number) => {
    const currentIndex = items.findIndex((item) => item.id === id);
    if (currentIndex < 0) return;
    const nextIndex = currentIndex + delta;
    if (nextIndex < 0 || nextIndex >= items.length) return;
    const nextItems = [...items];
    const [moved] = nextItems.splice(currentIndex, 1);
    nextItems.splice(nextIndex, 0, moved);
    void handleSaveOrder(nextItems);
  }, [handleSaveOrder, items]);

  const handleDrop = (targetId: string) => {
    if (!draggingId || draggingId === targetId) {
      setDraggingId('');
      return;
    }
    const fromIndex = items.findIndex((item) => item.id === draggingId);
    const toIndex = items.findIndex((item) => item.id === targetId);
    if (fromIndex < 0 || toIndex < 0) {
      setDraggingId('');
      return;
    }
    const nextItems = [...items];
    const [moved] = nextItems.splice(fromIndex, 1);
    nextItems.splice(toIndex, 0, moved);
    setDraggingId('');
    void handleSaveOrder(nextItems);
  };

  const handleDelete = (id: string) => {
    setDeleteTargetId(id);
  };

  const handleToggleActive = async (item: PinMessage, nextIsActive: boolean) => {
    if (nextIsActive && !item.isActive) {
      const activeCount = items.filter((row) => row.isActive).length;
      const totalCount = items.length;
      const employerLevel = user?.level ?? USER_LEVEL.TRIAL;
      if (
        emitPinMessageActiveLimitIfReached(activeCount, maxItems, totalCount, employerLevel)
      ) {
        return;
      }
    }
    setTogglingId(item.id);
    setError('');
    try {
      await pinMessageApi.update(item.id, { isActive: nextIsActive });
      setItems((prev) => prev.map((row) => (row.id === item.id ? { ...row, isActive: nextIsActive } : row)));
      showSuccessToast(t('toast.pinMessage.updateSuccess'));
    } catch (toggleError) {
      if (isLimitReachedError(toggleError)) {
        return;
      }
      const message = toggleError instanceof Error ? toggleError.message : t('pinMessage.error.save');
      setError(message);
      showErrorToast(message);
    } finally {
      setTogglingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) {
      return;
    }
    const id = deleteTargetId;
    setDeleteTargetId(null);
    setError('');
    try {
      await pinMessageApi.remove(id);
      showSuccessToast(t('toast.pinMessage.deleteSuccess'));
      await load();
    } catch (deleteError) {
      const message = deleteError instanceof Error ? deleteError.message : t('pinMessage.error.remove');
      setError(message);
      showErrorToast(message);
    }
  };

  if (!user) return null;
  if (user.role !== USER_ROLE.EMPLOYER) {
    return <Navigate to="/home" replace />;
  }

  return (
    <main className="px-4 py-5 pb-28">
      <AppPageTitleSection
        title={t('pinMessage.list.title')}
        subtitle={t('pinMessage.list.subtitle', { maxItems })}
      />

      {loading ? <p className="text-sm text-gray-500">{t('common.loading')}</p> : null}
      {error ? <p className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p> : null}
      {savingOrder ? <p className="mb-3 text-xs text-gray-500">{t('pinMessage.list.savingOrder')}</p> : null}

      {!loading && items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300">
          {t('pinMessage.list.empty')}
        </div>
      ) : null}

      <ul className="space-y-3">
        {items.map((item) => (
          <li
            key={item.id}
            draggable
            onDragStart={() => setDraggingId(item.id)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => handleDrop(item.id)}
            className="flex items-stretch gap-2 rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900"
          >
            <div className="flex shrink-0 flex-col justify-between">
              <button
                type="button"
                onClick={() => moveItem(item.id, -1)}
                disabled={savingOrder || orderedIds[0] === item.id}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-40 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                aria-label={t('pinMessage.action.moveUp')}
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => moveItem(item.id, 1)}
                disabled={savingOrder || orderedIds[orderedIds.length - 1] === item.id}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-40 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                aria-label={t('pinMessage.action.moveDown')}
              >
                ▼
              </button>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${pinMessageIconTheme(item.iconType).badge}`}
                >
                  {pinMessageIconSvg(item.iconType, 'h-3.5 w-3.5')}
                  <span>{iconLabel(item.iconType, t)}</span>
                </span>
                <p className="min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{item.title}</p>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-300">
                {visibilityLabel(item.visibility, t)}
              </p>
              {item.isActive ? (
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-300">
                  {windowLabel(item, t)}
                </p>
              ) : null}
              <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-300">
                <label className="inline-flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={item.isActive}
                    disabled={togglingId === item.id}
                    onChange={(event) => { void handleToggleActive(item, event.target.checked); }}
                    className="sr-only"
                    aria-label={t('pinMessage.field.status')}
                  />
                  <span
                    className={`relative h-5 w-12 rounded-full border transition ${
                      item.isActive
                        ? 'border-emerald-400 bg-emerald-400'
                        : 'border-gray-400 bg-gray-600'
                    } ${togglingId === item.id ? 'opacity-50' : ''}`}
                  >
                    <span
                      className={`absolute inset-y-0 flex items-center text-[10px] font-black leading-none ${
                        item.isActive ? 'left-2.5 text-gray-900' : 'right-2 text-white'
                      }`}
                    >
                      {item.isActive ? 'ON' : 'OFF'}
                    </span>
                    <span
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition ${
                        item.isActive ? 'left-7' : 'left-0.5'
                      }`}
                    />
                  </span>
                  <span>{item.isActive ? t('pinMessage.status.active') : t('pinMessage.status.inactive')}</span>
                </label>
              </div>
            </div>
            <div className="flex shrink-0 flex-col justify-center gap-2">
              <button
                type="button"
                onClick={() => navigate(`/pin-messages/${item.id}/edit`)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                aria-label={t('pinMessage.action.edit')}
              >
                <PencilIcon />
              </button>
              <button
                type="button"
                onClick={() => void handleDelete(item.id)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100 focus:outline-none focus:ring-2 focus:ring-rose-400 dark:border-rose-900/60 dark:bg-rose-950/50 dark:text-rose-100 dark:hover:bg-rose-950/80"
                aria-label={t('pinMessage.action.remove')}
              >
                <TrashIcon />
              </button>
            </div>
          </li>
        ))}
      </ul>

      <PagePrimaryFab
        onClick={() => {
          void guardNavigateToCreatePinMessage(navigate, '/pin-messages/new');
        }}
        ariaLabel={t('pinMessage.action.add')}
      >
        <span className="text-lg font-bold leading-none" aria-hidden>+</span>
        <span>{t('pinMessage.action.add')}</span>
      </PagePrimaryFab>

      <ConfirmDialog
        open={Boolean(deleteTargetId)}
        title={t('pinMessage.action.remove')}
        description={t('pinMessage.confirm.remove')}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => { void handleConfirmDelete(); }}
        onCancel={() => setDeleteTargetId(null)}
        variant="danger"
      />
    </main>
  );
};
